import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { restaurantLogin, requestPasswordReset, confirmPasswordReset } from '../menuApi';

/**
 * Restaurant sign-in: who you are, then a password. That's it.
 *
 * WHAT THIS REPLACED, AND WHY
 * ---------------------------
 * The old form asked for a NAME (used only as a label) plus an 8-character
 * mixed-case access code, in a password field, on a kitchen tablet — and it expired
 * every 12 hours. The code had to be that awkward because `findRestaurantByCode()`
 * matched a hash across every restaurant, so it doubled as the identifier and had
 * to be globally unique.
 *
 * Naming the restaurant up front breaks that constraint: the password only has to
 * be unique to one restaurant, so it can be four digits. And "stay signed in"
 * returns a 90-day device token, so in practice this is typed once per screen.
 *
 * Unlike a one-time pairing code, a password lets a restaurant sign in a new device
 * themselves at 7pm on a Friday without ringing RedSpeed — which is the actual
 * complaint this whole change exists to answer.
 */
function StaffLogin() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const handleLogin = async () => {
    if (!identifier || !password) {
      setError('Enter your restaurant and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await restaurantLogin(identifier.trim(), password, remember);
      // Go to the KITCHEN, not /staff. /staff is a mock jobs board carrying three
      // hardcoded fake jobs left over from an early concept — sending a restaurant
      // there meant they signed in successfully and then saw invented data instead
      // of their own orders, which is exactly what "the restaurant can't log in"
      // turned out to mean.
      navigate('/kitchen');
    } catch (err) {
      setError(err.message || 'Sign-in failed');
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={styles.card}
      >
        {/* The product's own logo — `Gap` + `Go` in accent, matching LandingPage's
            `.lg-logo` / `.lg-logo-go`. The parent brand is credited in the footer,
            not lashed to the logo. */}
        <div style={styles.wordmark}>
          <span style={{ color: RS.text }}>Gap</span>
          <span style={{ color: RS.accent }}>Go</span>
        </div>

        <h2 style={styles.title}>Restaurant sign in</h2>
        <p style={styles.subtitle}>Your kitchen's orders, for your restaurant only</p>

        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>RESTAURANT NAME OR EMAIL</label>
          <div style={styles.inputWrap}>
            <input
              style={styles.input}
              placeholder="Darcy's"
              value={identifier}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
            />
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>PASSWORD</label>
          <div style={styles.inputWrap}>
            <input
              style={styles.input}
              placeholder="••••"
              /* A numeric keypad on a tablet, and a show/hide toggle — the old
                 field was write-only, so a mistyped character meant starting the
                 whole code again with no way to see what went wrong. */
              type={show ? 'text' : 'password'}
              inputMode="numeric"
              autoComplete="current-password"
              maxLength={64}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <button
              type="button"
              style={styles.showBtn}
              onClick={() => setShow((v) => !v)}
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <label style={styles.remember}>
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            style={styles.checkbox}
          />
          <span>Keep this screen signed in</span>
        </label>

        {notice && !error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.noticeBox}
          >
            <span>✅</span>
            <span>{notice}</span>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.errorBox}
          >
            <span>⚠️</span>
            <span>{error}</span>
          </motion.div>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          style={{ ...styles.loginBtn, opacity: loading ? 0.6 : 1 }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </motion.button>

        <button style={styles.linkBtn} onClick={() => { setResetOpen(true); setError(''); }}>
          Forgotten your password?
        </button>
      </motion.div>

      {resetOpen && (
        <ResetDialog
          identifier={identifier}
          onClose={() => setResetOpen(false)}
          onDone={(who) => { setResetOpen(false); setIdentifier(who); setPassword(''); setError(''); setNotice('Password reset. Sign in with your new password.'); }}
        />
      )}

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        style={styles.footer}
      >
        A{' '}
        <span style={styles.redspeed}>
          <span style={{ color: RS.textMuted }}>Red</span>
          <span style={{ color: RS.accent }}>Speed</span>
        </span>{' '}
        product
      </motion.p>
    </div>
  );
}

/**
 * Forgotten password, in two steps on one screen.
 *
 * This replaces a `mailto:hello@redspeed.com.au` link — the entire previous answer to
 * a locked-out restaurant was "email us and wait", which for a kitchen at 7pm on a
 * Friday is the same as no answer.
 *
 * Step 1 NEVER reports whether the restaurant exists. The server replies 200 either
 * way so the endpoint cannot be used to enumerate RedSpeed's customers, and this
 * screen has to preserve that — hence the deliberately noncommittal "if that
 * restaurant exists" wording, and why it advances to step 2 regardless.
 */
function ResetDialog({ identifier: initial, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState(initial || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async () => {
    if (!identifier.trim()) { setError('Enter your restaurant name or email'); return; }
    setBusy(true); setError('');
    try {
      await requestPasswordReset(identifier.trim());
      setStep(2);
    } catch (e) {
      setError(e.message || 'Could not send a code right now');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!code.trim() || !password) { setError('Enter the code and a new password'); return; }
    if (password.length < 4) { setError('New password must be at least 4 characters'); return; }
    if (password !== confirm) { setError('The two passwords do not match'); return; }
    setBusy(true); setError('');
    try {
      await confirmPasswordReset(identifier.trim(), code.trim(), password);
      onDone(identifier.trim());
    } catch (e) {
      setError(e.message || 'Could not reset the password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.dialogTitle}>{step === 1 ? 'Reset your password' : 'Enter your code'}</h3>

        {step === 1 ? (
          <>
            <p style={styles.dialogBody}>
              We'll email a six-digit code to the billing address on your account.
            </p>
            <label style={styles.fieldLabel}>RESTAURANT NAME OR EMAIL</label>
            <input
              style={styles.input}
              value={identifier}
              autoFocus
              onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && sendCode()}
            />
          </>
        ) : (
          <>
            <p style={styles.dialogBody}>
              If that restaurant exists, a code is on its way. It expires in 30 minutes
              and can only be used once.
            </p>
            <label style={styles.fieldLabel}>SIX-DIGIT CODE</label>
            <input
              style={{ ...styles.input, letterSpacing: '6px', fontSize: '20px', textAlign: 'center' }}
              value={code}
              autoFocus
              inputMode="numeric"
              maxLength={6}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
            />
            <label style={styles.fieldLabel}>NEW PASSWORD</label>
            <input
              style={styles.input}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
            />
            <label style={styles.fieldLabel}>CONFIRM NEW PASSWORD</label>
            <input
              style={styles.input}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </>
        )}

        {error && <div style={styles.errorBox}><span>⚠️</span><span>{error}</span></div>}

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button style={styles.secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            style={{ ...styles.loginBtn, flex: 1, marginTop: 0, opacity: busy ? 0.6 : 1 }}
            onClick={step === 1 ? sendCode : submit}
            disabled={busy}
          >
            {busy ? 'Working…' : step === 1 ? 'Send code' : 'Reset password'}
          </button>
        </div>

        {step === 2 && (
          <button style={styles.linkBtn} onClick={() => { setStep(1); setError(''); }} disabled={busy}>
            Use a different restaurant
          </button>
        )}
      </div>
    </div>
  );
}

/* RedSpeed design tokens — mirrored from redspeed-website/app/globals.css.
   Change one, change both (and packages/brand/colors.js). */
const RS = {
  bg: '#F4F5F7',
  surface: '#FFFFFF',
  text: '#12141A',
  textMuted: '#565C6B',
  textFaint: '#858B99',
  accent: '#E8001D',
  green: '#067A4E',
  danger: '#E8001D',
  shadow: '0 4px 12px rgba(18,20,26,0.08)',
  glassBg: '#FFFFFF',
  glassBorder: '#E4E7EC',
};

const styles = {
  page: {
    minHeight: '100vh', background: RS.bg,
    // Full width, not a 430px phone column. This screen is opened on the kitchen
    // tablet, and the capped column left the dark `body` background (index.css)
    // showing as black gutters either side of a light card. The CARD is what stays
    // narrow — see `card.maxWidth`.
    width: '100%',
    fontFamily: "var(--font-inter, 'Inter'), -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    position: 'relative', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 16px', boxSizing: 'border-box',
  },
  card: {
    width: '100%', maxWidth: '430px', background: RS.glassBg,
    border: `1px solid ${RS.glassBorder}`,
    borderRadius: '24px', padding: '32px 24px',
    boxShadow: RS.shadow,
    position: 'relative', zIndex: 1, boxSizing: 'border-box',
  },
  // Matches LandingPage `.lg-logo` exactly so the product reads the same everywhere.
  wordmark: {
    fontSize: '22px', fontWeight: '800', letterSpacing: '-0.5px',
    lineHeight: 1, marginBottom: '26px',
  },
  title: {
    fontSize: '22px', fontWeight: '700',
    color: RS.text, margin: '0 0 6px', letterSpacing: '-0.02em',
  },
  subtitle: { fontSize: '13px', color: RS.textMuted, margin: '0 0 24px' },
  fieldGroup: {
    display: 'flex', flexDirection: 'column',
    gap: '8px', marginBottom: '16px',
  },
  fieldLabel: {
    fontSize: '10px', letterSpacing: '2px',
    color: RS.textFaint, fontWeight: '700',
  },
  inputWrap: {
    background: RS.glassBg, border: `1px solid ${RS.glassBorder}`,
    borderRadius: '14px', padding: '4px 16px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  input: {
    background: 'transparent', border: 'none', outline: 'none',
    color: RS.text, fontSize: '16px', width: '100%',
    padding: '12px 0', fontFamily: 'inherit',
  },
  showBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: RS.textMuted, fontSize: '12px', fontWeight: '600',
    fontFamily: 'inherit', padding: '4px', flexShrink: 0,
  },
  remember: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '13px', color: RS.textMuted, marginBottom: '20px',
    cursor: 'pointer', userSelect: 'none',
  },
  checkbox: { width: '17px', height: '17px', accentColor: RS.accent, cursor: 'pointer' },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(232,0,29,0.06)', border: '1px solid rgba(232,0,29,0.22)',
    borderRadius: '12px', padding: '12px 16px',
    fontSize: '13px', color: RS.danger, marginBottom: '16px',
  },
  // RS `.btn-red` — pill, 48px, the single accent element on the screen.
  loginBtn: {
    width: '100%', background: RS.accent,
    color: '#fff', border: 'none',
    borderRadius: '9999px', minHeight: '48px', padding: '0 28px',
    fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    marginBottom: '16px', letterSpacing: '-0.01em',
  },
  hint: { textAlign: 'center', fontSize: '12px', color: RS.textFaint, margin: 0, lineHeight: 1.6 },
  hintLink: { color: RS.textMuted, textDecoration: 'underline' },
  noticeBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(6,122,78,0.07)', border: '1px solid rgba(6,122,78,0.22)',
    borderRadius: '12px', padding: '12px 16px',
    fontSize: '13px', color: RS.green, marginBottom: '16px',
  },
  // A real control, not a mailto. Styled as a link so it stays visually subordinate
  // to the primary sign-in action.
  linkBtn: {
    display: 'block', width: '100%', textAlign: 'center', background: 'none', border: 'none',
    color: RS.textMuted, fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer',
    textDecoration: 'underline', padding: '6px 0', marginTop: '4px',
  },
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(18,20,26,0.45)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  },
  dialog: {
    width: '100%', maxWidth: '400px', maxHeight: '92vh', overflowY: 'auto',
    background: RS.surface, border: '1px solid #D0D5DD',
    borderRadius: '20px', padding: '26px 24px',
    boxShadow: '0 24px 60px rgba(18,20,26,0.18)',
  },
  dialogTitle: { fontSize: '19px', fontWeight: 700, color: RS.text, margin: '0 0 8px' },
  dialogBody: { fontSize: '13px', color: RS.textMuted, lineHeight: 1.6, margin: '0 0 18px' },
  secondaryBtn: {
    background: RS.surface, border: '1px solid #D0D5DD',
    color: RS.textMuted, borderRadius: '9999px', minHeight: '48px', padding: '0 22px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  footer: {
    fontSize: '11px', color: RS.textFaint,
    marginTop: '32px', position: 'relative', zIndex: 1,
  },
  redspeed: { fontWeight: '700', letterSpacing: '-0.2px' },
};

export default StaffLogin;
